import React from 'react';
import { Stack } from '@fluentui/react';
import { useStyles } from './index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import { EntityTagInput } from 'common/types/globalTypes';
import {
  PaymentAvailableTags,
  PaymentAvailableTagsVariables,
} from 'common/graphql/__generated__/PaymentAvailableTags';
import {
  GetPaymentTags,
  GetPaymentTagsVariables,
} from 'common/graphql/__generated__/GetPaymentTags';
import {
  EntityTagPaymentCreate,
  EntityTagPaymentCreateVariables,
} from 'common/graphql/__generated__/EntityTagPaymentCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import ENTITY_PAYMENT_CREATE from '../../../../../../graphql/EntityTagPaymentCreate.graphql';
import PAYMENT_AVAILABLE_TAGS from '../../../../../../graphql/PaymentAvailableTags.graphql';
import LIST_PAYMENT_TAGS from '../../../../../../graphql/GetPaymentTags.graphql';
import ENTITY_TAG_DELETE from '../../../../../../graphql/EntityTagDelete.graphql';

interface TagsProps {
  paymentId: string;
}

export const Tags: React.FC<TagsProps> = ({ paymentId }) => {
  const styles = useStyles();

  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: PaymentAvailableTags | undefined) =>
      data?.paymentAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: GetPaymentTags | undefined) =>
      data?.payment?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): PaymentAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): GetPaymentTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagPaymentCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  return (
    <Stack tokens={{ childrenGap: 15, padding: '24px 25px 0px' }}>
      <EntityTagsSection<
        PaymentAvailableTags,
        PaymentAvailableTagsVariables,
        GetPaymentTags,
        GetPaymentTagsVariables,
        EntityTagPaymentCreate,
        EntityTagPaymentCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={paymentId}
        getAvailableTagsGql={PAYMENT_AVAILABLE_TAGS}
        getEntityTagsGql={LIST_PAYMENT_TAGS}
        addTagGql={ENTITY_PAYMENT_CREATE}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
      />
    </Stack>
  );
};
